import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'

export type SchemaField = {
  key: string
  type: string
  component:
    | 'TextInput'
    | 'NumberInput'
    | 'Checkbox'
    | 'Textarea'
    | 'Select'
    | 'MultiSelect'
    | 'ArrayEditor'
    | 'JsonInput'
  required?: boolean
  nested?: Array<SchemaField>
  options?: Array<{ label: string; value: string }>
  validation?: unknown
}

export const getDefaultValueForType = (type: string): unknown => {
  switch (type) {
    case 'string':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'array':
      return []
    case 'object':
      return {}
    default:
      return null
  }
}

export const generateRuntimeSchema = (data: unknown): Array<SchemaField> => {
  if (!data || typeof data !== 'object') return []

  return Object.entries(data as { [key: string]: unknown }).map(
    ([key, value]) => ({
      key,
      type: Array.isArray(value) ? 'array' : typeof value,
      component:
        typeof value === 'boolean'
          ? 'Checkbox'
          : typeof value === 'number'
          ? 'NumberInput'
          : 'TextInput',
      required: value !== null && value !== undefined,
      nested:
        typeof value === 'object' && !Array.isArray(value) && value !== null
          ? generateRuntimeSchema(value)
          : undefined,
    }),
  )
}

export const convertStaticToSchemaField = (
  staticField: StaticFieldSchema,
): SchemaField => ({
  key: staticField.key,
  type: staticField.type,
  component: staticField.component,
  required: staticField.required,
  options: Array.isArray(staticField.options)
    ? typeof staticField.options[0] === 'object'
      ? // pass through object options as-is
        (staticField.options as Array<{ label: string; value: string }>)
      : // map string options to {label,value}
        (staticField.options as Array<string>).map((opt) => ({
          label: opt,
          value: opt,
        }))
    : undefined,
  nested: staticField.nested
    ? staticField.nested.map((nestedField) =>
        convertStaticToSchemaField(nestedField),
      )
    : undefined,
})

export const createMergedSchema = (
  staticSchema: Array<StaticFieldSchema> | undefined,
  runtimeSchema: Array<SchemaField>,
): Array<SchemaField> => {
  if (!staticSchema) {
    return runtimeSchema
  }

  const mergedFields: Array<SchemaField> = []
  const processedKeys = new Set<string>()

  for (const staticField of staticSchema) {
    const schemaField = convertStaticToSchemaField(staticField)
    mergedFields.push(schemaField)
    processedKeys.add(staticField.key)
  }

  for (const runtimeField of runtimeSchema) {
    if (!processedKeys.has(runtimeField.key)) {
      mergedFields.push(runtimeField)
    }
  }

  return mergedFields
}

// Recursively return a new schema array with fields sorted alphabetically by key
export const sortSchemaByKey = (
  fields: Array<SchemaField> | undefined,
): Array<SchemaField> => {
  if (!fields || fields.length === 0) return []
  const sorted = [...fields].sort((a, b) => a.key.localeCompare(b.key))
  return sorted.map((field) => {
    const next: SchemaField = {
      key: field.key,
      type: field.type,
      component: field.component,
      required: field.required,
      options: field.options,
      nested: field.nested ? sortSchemaByKey(field.nested) : undefined,
      validation: field.validation,
    }
    return next
  })
}

export const findSchemaField = (
  schema: Array<StaticFieldSchema> | undefined,
  path: string,
): StaticFieldSchema | undefined => {
  if (!schema) return undefined

  const findByPath = (
    fields: Array<StaticFieldSchema>,
    targetPath: string,
  ): StaticFieldSchema | undefined => {
    for (const field of fields) {
      if (field.fullPath === targetPath) {
        return field
      }
      if (field.nested) {
        const found = findByPath(field.nested, targetPath)
        if (found) return found
      }
    }
    return undefined
  }

  return findByPath(schema, path)
}
